import {Box, Typography as Typo, styled, TypographyProps} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {EmailBg, SurveyCheckGreen} from '@jobseeker/lotus/assets/icons';
import {AppState} from '@jobseeker/common-core/types';
import {shallowEqual, useSelector} from 'react-redux';
import {Dispatch, ReactNode, SetStateAction} from 'react';
import Button from '@jobseeker/lotus/elements/ButtonV2';
import userService from '@jobseeker/common-core/services/user.service';
import {enqueueSnackbar} from 'notistack';

const Typography = styled(Typo)<TypographyProps>(({}) => ({
  display: 'flex',
  alignItems: 'center',
}));

type VerificationProps = {
  setDialogOpen: Dispatch<SetStateAction<boolean>>;
  setTitle: Dispatch<SetStateAction<string>>;
  setSubTitle?: Dispatch<SetStateAction<string>>;
  setDialogContent: Dispatch<SetStateAction<ReactNode>>;
  setErrorText: Dispatch<SetStateAction<string>>;
  setShowEmailBanner: Dispatch<SetStateAction<boolean>>;
};

export const EmailVerification = ({
  setDialogContent,
  setDialogOpen,
  setTitle,
  setSubTitle,
  setErrorText,
  setShowEmailBanner,
}: VerificationProps) => {
  const {email} = useSelector(
    (state: AppState) => state.user.profile,
    shallowEqual
  );

  const handleResendEmail = async () => {
    try {
      const resInfo = await userService.sendVerifyEmail();

      if (resInfo?.error?.code == 429) {
        setShowEmailBanner(false);
        setErrorText(resInfo?.error?.message);
        return;
      }
      if (resInfo?.error?.message) {
        enqueueSnackbar('Failed to resend verification email', {
          variant: 'error',
        });
        return;
      }
      enqueueSnackbar('Verification email resent', {variant: 'success'});

      setTitle(
        `Please click the verification link resent to ${email} to verify your Email Address`
      );
      if (setSubTitle) setSubTitle('');
      setDialogContent(
        <>
          <Box display={'flex'} gap={1.5} mt={2} mb={2}>
            <Button
              onClick={() => {
                setDialogOpen(false);
              }}
              color="secondary"
              variant="contained"
            >
              Okay
            </Button>
          </Box>
        </>
      );

      setDialogOpen(true);
    } catch (error) {
      enqueueSnackbar('An error occurred while resending the email', {
        variant: 'error',
      });
    }
  };
  return (
    <Box maxHeight={'calc(80vh - 60px)'} mt={1} borderRadius={2}>
      <Box
        sx={{
          paddingTop: {xxs: 2, md: 2.25},
          paddingLeft: {xxs: 1.5, md: 1.5},
          paddingRight: {xxs: 1.5, md: 2},
          paddingBottom: {xxs: 2, md: 2},
          background: '#91B87929',
        }}
        display={'flex'}
        justifyContent={'flex-start'}
      >
        <Box>
          <Image src={EmailBg} width={39} height={39} alt="email" />
        </Box>
        <Box display={'flex'} flexDirection={'column'} ml={1.5}>
          <Typography
            variant="body2"
            color="text.primary"
            lineHeight={'18px'}
            sx={{display: 'inline', whiteSpace: {xxs: 'normal', md: 'nowrap'}}}
          >
            {`We have sent you a verification link on your Email-ID `}
            <Typography
              variant="body2"
              fontWeight={600}
              component="span"
              sx={{display: 'inline'}}
            >
              {email}
            </Typography>
          </Typography>
          <Typography
            mt={0.25}
            variant="subtitle3"
            lineHeight={'16px'}
            color="var(--Grey-font-Color, #7E7E7E)"
          >
            Please check your inbox
          </Typography>

          <Box sx={{display: {xxs: 'block', md: 'flex'}}} mt={{xxs: 3, md: 2}}>
            <Typography
              variant="subtitle3"
              color="var(--Grey-font-Color, #7E7E7E)"
            >
              Didn’t receive the email?&nbsp;
            </Typography>
            <Typography
              onClick={handleResendEmail}
              sx={{
                display: 'inline',
                padding: '0px',
                fontSize: '12px',
                lineHeight: '16px',
                fontWeight: '500',
                color: 'primary.main',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              Resend Verification email
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
